import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { sevJobAt, sevOnLine, SEV_GATE, SEV_PILE, SEV_RUNNEL, type BattleState, type SeventhRules } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

/** A heap of rubble: stones piled, one fallen off. */
const RUBBLE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 13.5l2.6-4.2 2.2 1.2 2.3-4.4 3 3.2 2.9 4.2z" fill="currentColor"/><rect x="11.4" y="11.2" width="2.6" height="2.3" rx="0.4" fill="currentColor" opacity="0.7"/></svg>';
/** A sluice gate: a channel, the gate in it, its wheel. */
const GATE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 13h12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><rect x="5" y="6" width="6" height="5.5" rx="0.6" fill="currentColor"/><circle cx="8" cy="3" r="1.8" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>';
/** The cistern: a round basin, water in it. */
const CISTERN = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="5" rx="6" ry="2.2" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M2 5v6c0 1.2 2.7 2.2 6 2.2s6-1 6-2.2V5" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M2.6 8.5c1.6.8 3.4 1.2 5.4 1.2s3.8-.4 5.4-1.2V11c0 1-2.4 1.8-5.4 1.8S2.6 12 2.6 11z" fill="currentColor"/></svg>';
/** A handful of salt. */
const SALT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 12.5c1.4-3.2 3.6-5 6-5s4.6 1.8 6 5z" fill="currentColor"/><circle cx="4" cy="5" r="1" fill="currentColor"/><circle cx="7.5" cy="3.2" r="1" fill="currentColor"/><circle cx="11.6" cy="4.6" r="1" fill="currentColor"/></svg>';
/** The seventh altar's eye. */
const EYE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 8C3.3 4.8 5.6 3.4 8 3.4s4.7 1.4 6.5 4.6C12.7 11.2 10.4 12.6 8 12.6S3.3 11.2 1.5 8z" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="8" r="2.2" fill="currentColor"/></svg>';
/** La Que Huyó's run: a straight line, an arrowhead. */
const RUN = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 8h9.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M9.5 4l4.5 4-4.5 4z" fill="currentColor"/></svg>';
/** A squire: a small shield. */
const SQUIRE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2l4.5 1.6V8c0 2.8-2 4.8-4.5 6-2.5-1.2-4.5-3.2-4.5-6V3.6z" fill="currentColor" opacity="0.8"/></svg>';

type Mark = { el: HTMLButtonElement; state: HTMLElement; at: number };

/**
 * The seventh altar on screen (docs/sendas/paladin.md, chapter 5), inside a path's own plate (game/senda-hud.ts) and
 * over the board; a phone's plate kept under about a quarter of the screen:
 *
 * - the rubble (`rubble`): the heaps, each one's work so far, and the bone singer when one is up; a mark on each heap
 *   left, its ring filling as it is worked.
 * - the well (`well`, the test): the cistern's ten marks with the island's line at four and the ring's at two; the
 *   spring's next mark; the four gates and what each takes («−3»), running or still; «the ring is too low» red; the
 *   squires (only seen in the water). A mark on each gate.
 * - La Que Huyó (`fled`): her phase (she runs, she hides, she stays), the oath's bar (Marit praying, or rising only
 *   with the hero at the altar), the line's warning red while one is marked, and whether the hero stands on it; the
 *   gates (open, shut, held by the knights) and the salt runnels, marked over the board while she hides.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own layout.
 */
export class SeventhHud {
  readonly panel: HTMLElement;
  private kind: SeventhRules['kind'];
  private v = new THREE.Vector3();
  private marks: Mark[] = [];
  private points: THREE.Vector3[] = [];
  private key = '';
  // The rubble.
  private pileRow = h('div.bh-row.sv-piles');
  private pilePips: HTMLElement[] = [];
  private pileText = h('span.al-wave');
  private singerRow = h('div.bh-row.al-waverow.sv-singer');
  // The well.
  private poolRow = h('div.bh-row.sv-pool');
  private poolPips: HTMLElement[] = [];
  private poolText = h('span.al-wave');
  private gatesRow = h('div.bh-row.sv-gates');
  private gateChips: { el: HTMLElement; n: HTMLElement }[] = [];
  private springRow = h('div.bh-row.al-waverow.sv-spring');
  private springText = h('span.al-wave');
  // The fight.
  private oathRow = h('div.bh-row.sv-oath');
  private oathFill = h('i');
  private oathText = h('span.al-wave');
  private runRow = h('div.bh-row.al-waverow.sv-run');
  private runText = h('span.al-wave');

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const r = s.rules?.senda?.seventh;
    this.kind = r?.kind ?? 'rubble';
    const rows: HTMLElement[] = [];
    if (r?.rubble) {
      this.pilePips = r.rubble.piles.map(() => h('i'));
      this.pileRow.append(h('span.bh-ico', { html: RUBBLE }), h('span.al-pips.sv-heaps', {}, ...this.pilePips), this.pileText);
      this.singerRow.append(h('span.bh-ico', { html: ICONS.skull }), h('span.al-wave', {}, t('senda.hud.seventh.singer')));
      this.singerRow.hidden = true;
      rows.push(this.pileRow, this.singerRow);
      r.rubble.piles.forEach((p, i) => {
        this.addMark(SEV_PILE + i, '.sv-heap', `sev-pile-${i}`, t('senda.hud.seventh.pile'), RUBBLE, '');
        this.points.push(simToWorld(p.x, p.y, new THREE.Vector3(), 0.4));
      });
    }
    const W = r?.well;
    if (r && W && r.kind === 'well') {
      this.poolPips = Array.from({ length: W.cap }, (_, i) => h(i === W.bare - 1 ? 'i.bare' : i === W.ring - 1 ? 'i.ring' : 'i'));
      this.poolRow.append(h('span.bh-ico', { html: CISTERN }), h('span.al-pips.mn-water.sv-water', {}, ...this.poolPips), this.poolText);
      this.gateChips = W.gates.map((_, g) => {
        const n = h('small', {}, t('senda.hud.seventh.take', { n: W.take[g] ?? 0 }));
        const el = h('span.mn-chip', {}, h('b', {}, t(`senda.hud.seventh.gate.${g}`)), n);
        return { el, n };
      });
      this.gatesRow.append(h('span.bh-ico', { html: GATE }), h('span.mn-chips', {}, ...this.gateChips.map((c) => c.el)));
      this.springRow.append(h('span.bh-ico', { html: SQUIRE }), this.springText);
      rows.push(this.poolRow, this.gatesRow, this.springRow);
    }
    if (r && W) {
      W.gates.forEach((p, g) => {
        this.addMark(SEV_GATE + g, `.mn-gate.sv-gate.g${g}`, `sev-gate-${g}`, t(`senda.hud.seventh.gate.${g}`), GATE, t(`senda.hud.seventh.gate.${g}`));
        this.points.push(simToWorld(p.x, p.y, new THREE.Vector3(), 0.3));
      });
    }
    const F = r?.fled;
    if (r && F) {
      this.oathRow.append(h('span.bh-ico', { html: EYE }), h('span.bh-label', {}, t('senda.hud.seventh.oath')), h('span.bar.sv-oath-bar', {}, this.oathFill), this.oathText);
      this.runRow.append(h('span.bh-ico', { html: RUN }), this.runText);
      rows.push(this.oathRow, this.runRow);
      F.runnels.forEach((_, k) => {
        const p = sevJobAt(r, SEV_RUNNEL + k);
        this.addMark(SEV_RUNNEL + k, '.mn-spot.sv-runnel', `sev-runnel-${k}`, t('senda.hud.seventh.runnel'), SALT, '');
        this.points.push(p ? simToWorld(p.x, p.y, new THREE.Vector3(), 0.05) : new THREE.Vector3());
      });
    }
    this.panel = h(`div.bh.sv-panel.sv-${this.kind}`, {}, ...rows);
  }

  private addMark(at: number, cls: string, test: string, hint: string, glyph: string, name: string): void {
    const state = h('small.al-state');
    const el = h(
      `button.al-stone.mn-mark${cls}`,
      { type: 'button', 'data-test': test, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
      h('span.al-ring'),
      h('span.al-glyph', { html: glyph }),
      name ? h('b.mn-name', {}, name) : '',
      state,
    ) as HTMLButtonElement;
    this.board.append(el);
    this.marks.push({ el, state, at });
  }

  private setState(m: Mark, text: string): void {
    if (m.state.textContent === text) return;
    m.state.textContent = text;
    remeasureMark(m.el);
  }

  private text(el: HTMLElement, text: string): void {
    if (el.textContent !== text) el.textContent = text;
  }

  update(s: BattleState): void {
    const r = s.rules?.senda?.seventh;
    const w = s.senda?.seventh;
    if (!r || !w) return;
    const secs = (ticks: number): number => Math.max(1, Math.ceil(ticks / TICK_HZ));
    const running = s.result === 'running';
    const R = r.rubble;
    if (R) {
      let n = 0;
      this.pilePips.forEach((p, i) => {
        const k = Math.min(1, (w.cleared[i] ?? 0) / R.stand);
        if (k >= 1) n++;
        p.classList.toggle('on', k >= 1);
        p.classList.toggle('part', k > 0 && k < 1);
      });
      this.text(this.pileText, t('senda.hud.seventh.piles', { n, of: R.piles.length }));
      this.singerRow.hidden = !s.enemies.some((e) => e.hp > 0 && e.kind === 'bonesinger');
      for (const m of this.marks) {
        const i = m.at - SEV_PILE;
        const k = Math.min(1, (w.cleared[i] ?? 0) / R.stand);
        m.el.classList.toggle('gone', k >= 1);
        m.el.classList.toggle('sent', k > 0 && k < 1);
        m.el.style.setProperty('--k', k.toFixed(2));
        m.el.disabled = !running || k >= 1;
      }
      return;
    }
    const W = r.well as NonNullable<SeventhRules['well']>;
    if (r.kind === 'well') {
      const key = `${w.level}|${w.open.join('')}`;
      if (key !== this.key) {
        this.key = key;
        this.poolPips.forEach((p, i) => p.classList.toggle('on', i < w.level));
        this.gateChips.forEach((c, g) => c.el.classList.toggle('open', (w.open[g] ?? 0) > 0));
        const bare = w.level <= W.bare;
        this.text(this.poolText, bare && w.level >= W.ring ? t('senda.hud.seventh.island') : t('senda.hud.seventh.level', { n: w.level, of: W.cap }));
        this.poolRow.classList.toggle('bare', bare && w.level >= W.ring);
        this.poolRow.classList.toggle('low', w.level < W.ring);
        for (const m of this.marks) {
          const g = m.at - SEV_GATE;
          const open = (w.open[g] ?? 0) > 0;
          m.el.classList.toggle('open', open);
          this.setState(m, open ? `${t('senda.hud.seventh.running')} ×${w.open[g]}` : t('senda.hud.seventh.take', { n: W.take[g] ?? 0 }));
        }
      }
      const flowing = w.open.some((x) => x > 0);
      const low = !flowing && w.level < W.ring;
      const spring = low ? t('senda.hud.seventh.tooLow') : flowing ? t('senda.hud.seventh.shields') : t('senda.hud.seventh.spring', { s: secs(W.spring - w.acc) });
      this.text(this.springText, spring);
      this.springRow.classList.toggle('warn', low);
      for (const m of this.marks) {
        const sent = w.job === m.at;
        m.el.classList.toggle('sent', sent);
        m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, W.turn)).toFixed(2) : '0');
        m.el.disabled = !running;
      }
      return;
    }
    const F = r.fled as NonNullable<SeventhRules['fled']>;
    // The oath's bar and who raises it.
    const k = Math.min(1, w.oath / Math.max(1, F.oath));
    setStyle(this.oathFill, 'width', `${(k * 100).toFixed(1)}%`);
    const hero = s.heroes.find((x) => x.downTicks === 0);
    const atAltar = !!hero && (hero.x - F.altar.x) ** 2 + (hero.y - F.altar.y) ** 2 <= F.near * F.near;
    const oath = w.fell >= 0 && k < 1 ? t('senda.hud.seventh.calm') : F.together ? t('senda.hud.seventh.oathPrays') : t('senda.hud.seventh.oathAtAltar');
    this.text(this.oathText, oath);
    this.oathRow.classList.toggle('rising', F.together || atAltar || w.fell >= 0);
    this.oathRow.classList.toggle('full', k >= 1);
    // Her run: the line marked (and whether the hero stands on it), hidden, still.
    const line = w.act === 1 && w.line.length === 4;
    const on = line && !!hero && sevOnLine(F, hero, w.line);
    const run = line
      ? on
        ? t('senda.hud.seventh.covered')
        : w.echo.length === 4
          ? t('senda.hud.seventh.chargeTwo')
          : t('senda.hud.seventh.charge')
      : w.act === 4
        ? t('senda.hud.seventh.still')
        : w.hidden > s.tick
          ? t('senda.hud.seventh.hidden')
          : t(`senda.hud.seventh.phase.${w.phase}`);
    this.text(this.runText, run);
    this.runRow.classList.toggle('warn', line && !on);
    this.runRow.classList.toggle('on', on);
    this.runRow.classList.toggle('still', w.act === 4);
    // The marks: gates open (to shut) or held; runnels while she hides, unsalted.
    for (const m of this.marks) {
      if (m.at >= SEV_RUNNEL) {
        const i = m.at - SEV_RUNNEL;
        const salted = !!w.salt[i];
        m.el.classList.toggle('salted', salted);
        m.el.classList.toggle('off', w.phase !== 1 && !salted);
        m.el.disabled = !running || salted;
      } else {
        const g = m.at - SEV_GATE;
        const open = (w.gate[g] ?? 0) === 1;
        const held = g === F.held && s.senda?.npcs.some((n) => (n.id === 'orderKnight' || n.id === 'orderKnight2') && n.hp > 0);
        m.el.classList.toggle('open', open);
        m.el.classList.toggle('held', !!held);
        m.el.classList.toggle('off', !open && !held && w.phase === 0);
        this.setState(m, held ? t('senda.hud.seventh.gateHeld') : open ? t('senda.hud.seventh.gateOpen') : '');
        m.el.disabled = !running;
      }
      const sent = w.job === m.at;
      const need = m.at >= SEV_RUNNEL ? F.salt.stand : F.gate.close;
      m.el.classList.toggle('sent', sent);
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, need)).toFixed(2) : '0');
    }
  }

  /** Every frame: the marks over their places. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    this.marks.forEach((m, i) => {
      const p = this.points[i];
      if (!p || m.el.classList.contains('off') || m.el.classList.contains('gone')) return setStyle(m.el, 'display', 'none');
      this.v.copy(p).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) return setStyle(m.el, 'display', 'none');
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 52, Math.max(52, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    });
  }
}
